import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { sortHomeHealthTemplates } from "../../util/homeHealthTemplateDisplay";

// Template name constants
export const VISIT_EXAM_TEMPLATE_NAME = "Visit + Exam";
export const HIDDEN_PREFIX = "[HIDDEN]";
export const AT_RISK_REVIEW_TEMPLATE_LEVEL_1 =
  "[HIDDEN] [SPECIAL] At Risk Review Level 1";
export const AT_RISK_REVIEW_TEMPLATE_LEVEL_2 =
  "[HIDDEN] [SPECIAL] At Risk Review Level 2";
export const AT_RISK_REVIEW_TEMPLATE_LEVEL_3 =
  "[HIDDEN] [SPECIAL] At Risk Review Level 3";

/**
 * Template names that are only shown to Home Health External users.
 * In Navigator and regular CIP scribe, these are hidden from regular CIP users.
 */
export const HOME_HEALTH_ONLY_TEMPLATE_NAMES = [
  "Initial Provider Visit",
  "Follow Up",
  "NP - Initial Visit",
  "NP - Follow Up",
  "RN - Initial Visit",
  "RN - Initial Visit (No PE)",
  "RN - Follow Up",
  "RN - Follow Up (No PE)",
  "SW - Initial Visit",
  "SW - Follow Up",
  "Peds NP - Initial Visit",
  "Peds NP - Follow Up",
  "Peds RN - Initial Visit",
  "Peds RN - Initial Visit (No PE)",
  "Peds RN - Follow Up",
  "Peds RN - Follow Up (No PE)",
  "Peds SW - Initial Visit",
  "Peds SW - Follow Up",
  "Chap - Initial Visit",
  "Chap - Follow Up",
  "OASIS - Compassus",
];

/** Returns true if template name matches the home-health-only list (case-insensitive partial match). */
export const isHomeHealthOnlyTemplateName = (templateName: string): boolean => {
  const lowerTemplateName = templateName.toLowerCase();
  return HOME_HEALTH_ONLY_TEMPLATE_NAMES.some((name) =>
    lowerTemplateName.includes(name.toLowerCase())
  );
};

/**
 * For Navigator/regular scribe:
 * - Home Health External: only see templates in the allow-list (same as VisitNoteEditor).
 * - Regular CIP: only see templates NOT in that list (home-health-only templates hidden).
 */
export function filterTemplatesByUserType<T extends { name?: string | null }>(
  templates: T[],
  isHomeHealthExternal: boolean
): T[] {
  if (isHomeHealthExternal) {
    return sortHomeHealthTemplates(
      templates.filter((t) => isHomeHealthOnlyTemplateName(t.name ?? ""))
    );
  }
  return templates.filter((t) => !isHomeHealthOnlyTemplateName(t.name ?? ""));
}

// Type reference to HPI Template repository types
interface IHPITemplate {
  id: number;
  status: number;
  name: string;
  template: string;
  clinic_id: number;
  created_by: number;
  created_at: string;
  updated_at: string;
}

interface HPITemplateState {
  selectedTemplateId: number | null;
  selectedTemplateName: string | null;
  templates: IHPITemplate[];
  isLoading: boolean;
  error: string | null;
}

const initialState: HPITemplateState = {
  selectedTemplateId: null,
  selectedTemplateName: null,
  templates: [],
  isLoading: false,
  error: null,
};

const hpiTemplateSlice = createSlice({
  name: "hpiTemplate",
  initialState,
  reducers: {
    setTemplates: (state, action: PayloadAction<IHPITemplate[]>) => {
      state.templates = action.payload;
      state.isLoading = false;
      state.error = null;
    },

    setSelectedTemplate: (
      state,
      action: PayloadAction<{ id: number; name: string }>
    ) => {
      state.selectedTemplateId = action.payload.id;
      state.selectedTemplateName = action.payload.name;
    },

    clearSelectedTemplate: (state) => {
      state.selectedTemplateId = null;
      state.selectedTemplateName = null;
    },

    initializeDefaultTemplate: (
      state,
      action: PayloadAction<{ templates: IHPITemplate[]; clinicId: number }>
    ) => {
      const { templates } = action.payload;

      if (templates.length === 0) {
        state.selectedTemplateId = null;
        state.selectedTemplateName = null;
        return;
      }

      // Try to find global template with name "Visit + Exam"
      const globalVisitExamTemplate = templates.find(
        (t) =>
          (t.clinic_id === 0 || t.clinic_id === null) &&
          t.name === VISIT_EXAM_TEMPLATE_NAME
      );

      const templateToSelect = globalVisitExamTemplate || templates[0];

      state.selectedTemplateId = templateToSelect.id;
      state.selectedTemplateName = templateToSelect.name;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    addTemplate: (state, action: PayloadAction<IHPITemplate>) => {
      state.templates.push(action.payload);
    },

    updateTemplate: (state, action: PayloadAction<IHPITemplate>) => {
      const index = state.templates.findIndex(
        (t: IHPITemplate) => t.id === action.payload.id
      );
      if (index !== -1) {
        state.templates[index] = action.payload;
      }
    },

    removeTemplate: (state, action: PayloadAction<number>) => {
      state.templates = state.templates.filter(
        (t: IHPITemplate) => t.id !== action.payload
      );
      if (state.selectedTemplateId === action.payload) {
        state.selectedTemplateId = null;
        state.selectedTemplateName = null;
      }
    },

    resetHPITemplateState: () => initialState,
  },
});

export const {
  setTemplates,
  setSelectedTemplate,
  clearSelectedTemplate,
  initializeDefaultTemplate,
  setLoading,
  setError,
  addTemplate,
  updateTemplate,
  removeTemplate,
  resetHPITemplateState,
} = hpiTemplateSlice.actions;

// Selectors
export const selectSelectedTemplateId = (state: {
  hpiTemplate: HPITemplateState;
}) => state.hpiTemplate.selectedTemplateId;

export const selectSelectedTemplateName = (state: {
  hpiTemplate: HPITemplateState;
}) => state.hpiTemplate.selectedTemplateName;

export const selectHPITemplates = (state: { hpiTemplate: HPITemplateState }) =>
  state.hpiTemplate.templates;

export const selectSelectedTemplate = (state: {
  hpiTemplate: HPITemplateState;
}) => {
  const templateId = state.hpiTemplate.selectedTemplateId;
  if (!templateId) return null;
  return state.hpiTemplate.templates.find((t) => t.id === templateId) || null;
};

export const selectHPITemplateLoading = (state: {
  hpiTemplate: HPITemplateState;
}) => state.hpiTemplate.isLoading;

export const selectHPITemplateError = (state: {
  hpiTemplate: HPITemplateState;
}) => state.hpiTemplate.error;

export default hpiTemplateSlice.reducer;
